import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchBrief } from '../lib/data.js';
import { etToday, isStale } from '../lib/format.js';

// The background of every guest page: an engraved chart of Charleston Harbor
// in the margins (a band behind the masthead on a narrow screen), with the
// water-lines moving on today's tide, Charleston Light flashing its real
// rhythm, and the rose's arrow on today's wind. The drawing, its sources and
// its rules are in src/lib/harbor-chart.js.
//
// It was the live front page's alone until James, the day it shipped: "I
// would think we also want the background on other pages". So it is mounted
// once, beside the routes (App.jsx), and stays put while a reader moves
// between pages: on a wide screen the margins do not even redraw, and on a
// narrow one the band is fitted to the new page's masthead. The admin pages,
// wider than the column the chart fades out before, stay plain.
//
// The tide and the wind are today's, from the brief request the front page
// and the stay page already make (fetchBrief shares it), so an archive page
// shows the harbor as it is now, not as it was that day. A brief more than a
// day old, such as the fallback copy, moves nothing: no tide, no wind. The
// drawing code (its own chunk, by the dynamic import), the geometry and the
// brief are fetched once the page itself has loaded and the browser is idle,
// so none of it competes with a stay page's photographs; if the chart fails,
// the page is plain paper.
export default function HarborChart() {
  const ref = useRef(null);
  const chart = useRef(null);
  const { pathname } = useLocation();
  const admin = pathname.startsWith('/admin');

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof window.matchMedia !== 'function') return undefined;
    let cancelled = false;
    const load = () =>
      Promise.all([
        import('../lib/harbor-chart.js'),
        fetch('/data/harbor-chart.json').then((res) => (res.ok ? res.json() : null)),
        fetchBrief().catch(() => null)
      ])
        .then(([{ mountHarborChart }, data, brief]) => {
          if (cancelled || !data) return;
          const fresh = !!brief && !isStale(brief.generatedAt);
          chart.current = mountHarborChart(el, data, {
            tides: fresh ? brief.tides : null,
            wind: fresh ? brief.weatherDetails?.wind : null,
            live: fresh && brief.date === etToday(),
            header: () => document.querySelector('header')
          });
        })
        .catch(() => {});
    const idle = typeof window.requestIdleCallback === 'function';
    let handle = 0;
    const whenIdle = () => {
      handle = idle ? window.requestIdleCallback(load, { timeout: 1500 }) : window.setTimeout(load, 200);
    };
    if (document.readyState === 'complete') whenIdle();
    else window.addEventListener('load', whenIdle, { once: true });
    // The page under the band can change height without a new route (the
    // front page's brief arriving in place of its placeholder), so the chart
    // checks whether its masthead moved. It does nothing when it has not.
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(() => chart.current?.refresh()) : null;
    ro?.observe(document.body);
    return () => {
      cancelled = true;
      window.removeEventListener('load', whenIdle);
      if (idle) window.cancelIdleCallback(handle);
      else window.clearTimeout(handle);
      ro?.disconnect();
      chart.current?.destroy();
      chart.current = null;
    };
  }, [admin]);

  // A new page brings its own masthead.
  useEffect(() => {
    chart.current?.refresh();
  }, [pathname]);

  if (admin) return null;

  // Behind everything (z-index -1 in the root stacking context, above the
  // page's paper colour), untouchable, and silent to assistive tech. It fades
  // in once the chart is drawn; the renderer sets its position and height.
  return (
    <div
      ref={ref}
      aria-hidden="true"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: 260,
        zIndex: -1,
        pointerEvents: 'none',
        overflow: 'hidden',
        opacity: 0,
        transition: 'opacity 1.6s ease'
      }}
    />
  );
}
